import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import MobileBottomNav from '../components/MobileBottomNav';
import SEOSchema from '../components/SEOSchema';
import { medicalTeam } from '../data/mock';
import {
  ChevronRight, ShieldCheck, ClipboardCheck, RefreshCw, BookOpen,
  Stethoscope, Users, ArrowRight, GraduationCap,
} from 'lucide-react';

const principles = [
  {
    icon: Stethoscope,
    title: 'Doctor-led review',
    body: 'Health, nutrition and fitness content is written or reviewed by qualified medical and nutrition professionals before publication. Our medical lead is a practising physician.',
  },
  {
    icon: ClipboardCheck,
    title: 'Evidence-based',
    body: 'Guidance reflects current clinical understanding and established nutrition science. We avoid fads, guarantees and sensational claims, and we clearly separate general education from personalized medical advice.',
  },
  {
    icon: RefreshCw,
    title: 'Reviewed & updated',
    body: 'Articles carry a medical-review byline and are revised as understanding evolves. If you spot an error, contact us and we will review and correct it promptly.',
  },
  {
    icon: ShieldCheck,
    title: 'Responsible & honest',
    body: 'We never fabricate credentials, testimonials or outcomes. Individual results vary, and we always encourage you to continue care with your treating doctor.',
  },
];

const EditorialPolicyPage = () => (
  <div className="min-h-screen bg-slate-950">
    <Helmet>
      <title>Editorial & Medical Review Policy | TCUBE FITNESS™</title>
      <meta name="description" content="How TCUBE FITNESS creates, reviews and updates its health, nutrition and fitness content, our medical review process, author credentials, and correction policy." />
      <link rel="canonical" href="https://tcubefitness.com/editorial-policy" />
    </Helmet>

    <SEOSchema
      type="webpage"
      name="Editorial & Medical Review Policy"
      description="Our medical review process, author credentials and correction policy."
      url="/editorial-policy"
      breadcrumbs={[
        { name: 'Home', path: '/' },
        { name: 'Editorial & Medical Review Policy', path: '/editorial-policy' },
      ]}
    />

    <AnnouncementBanner />
    <Header />

    <main id="main-content" tabIndex={-1} className="pt-32 sm:pt-36 pb-24 lg:pb-16" data-testid="editorial-policy-page">
      <div className="container mx-auto px-4 sm:px-6">

        <nav aria-label="Breadcrumb" className="mb-6 text-xs sm:text-sm">
          <ol className="flex items-center gap-1.5 text-slate-500 flex-wrap">
            <li><Link to="/" className="hover:text-white">Home</Link></li>
            <li><ChevronRight className="w-3.5 h-3.5" aria-hidden="true" /></li>
            <li className="text-slate-300">Editorial & Medical Review Policy</li>
          </ol>
        </nav>

        {/* Hero */}
        <section className="max-w-3xl mx-auto text-center mb-14">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium mb-5">
            <ShieldCheck className="w-4 h-4" aria-hidden="true" /> Trust & Transparency
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
            Editorial & <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">Medical Review</span> Policy
          </h1>
          <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
            Health information should be accurate, honest and responsible. Here is how we create, review and maintain
            everything you read on TCUBE FITNESS™.
          </p>
        </section>

        {/* Principles */}
        <section className="max-w-5xl mx-auto grid sm:grid-cols-2 gap-5 mb-16" aria-label="Editorial principles">
          {principles.map((p) => (
            <div key={p.title} className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6" data-testid={`policy-principle-${p.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-4">
                <p.icon className="w-5 h-5" aria-hidden="true" />
              </div>
              <h2 className="text-lg font-bold text-white mb-2">{p.title}</h2>
              <p className="text-slate-400 text-sm leading-relaxed">{p.body}</p>
            </div>
          ))}
        </section>

        {/* Review process */}
        <section className="max-w-3xl mx-auto mb-16" aria-labelledby="process-heading">
          <div className="flex items-center gap-2.5 mb-6">
            <BookOpen className="w-5 h-5 text-emerald-400" aria-hidden="true" />
            <h2 id="process-heading" className="text-2xl sm:text-3xl font-bold text-white">Our review process</h2>
          </div>
          <ol className="space-y-4">
            {[
              'A topic is researched using current clinical guidance and reputable nutrition and exercise science.',
              'A draft is written in plain, non-alarmist language, separating general education from individual medical advice.',
              'A qualified medical or nutrition professional reviews the content for accuracy and safety.',
              'The published article carries a medical-review byline naming the reviewer and their credentials.',
              'Content is revisited and updated as evidence changes, and corrected promptly if an error is reported.',
            ].map((step, i) => (
              <li key={i} className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 font-bold flex items-center justify-center text-sm">{i + 1}</span>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed pt-1">{step}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Authors / reviewers */}
        <section className="max-w-5xl mx-auto mb-16" aria-labelledby="authors-heading">
          <div className="flex items-center gap-2.5 mb-6">
            <Users className="w-5 h-5 text-emerald-400" aria-hidden="true" />
            <h2 id="authors-heading" className="text-2xl sm:text-3xl font-bold text-white">Our authors & reviewers</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {medicalTeam.filter((m) => !m.isNetwork).map((m) => (
              <div key={m.id} className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6" data-testid={`policy-author-${m.id}`}>
                <h3 className="text-lg font-bold text-white">{m.name}</h3>
                <p className="text-emerald-300 text-sm font-semibold mb-2">{m.role}</p>
                {m.qualifications && (
                  <p className="inline-flex items-center gap-1.5 text-amber-200 text-xs font-semibold bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1 mb-3">
                    <GraduationCap className="w-3.5 h-3.5" aria-hidden="true" /> {m.qualifications}
                  </p>
                )}
                <p className="text-slate-400 text-sm leading-relaxed">{m.specialty}</p>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <Link to="/our-team" className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-semibold text-sm" data-testid="policy-team-link">
              Meet the full medical team <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </section>

        {/* Corrections + disclaimer */}
        <section className="max-w-3xl mx-auto">
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6" role="note" data-testid="policy-corrections">
            <h2 className="text-sm sm:text-base font-bold text-amber-200 mb-2 uppercase tracking-wider">Corrections & medical disclaimer</h2>
            <p className="text-slate-300 text-sm leading-relaxed mb-3">
              To report an inaccuracy, email <a href="mailto:support@tcubefitness.com" className="text-amber-200 underline underline-offset-2">support@tcubefitness.com</a> and we will review it.
              Our content is for general education and is not a substitute for professional medical advice, diagnosis or treatment.
              Always consult a qualified healthcare professional about your individual situation.
            </p>
            <Link to="/medical-disclaimer" className="text-amber-200 hover:text-amber-100 text-sm font-semibold underline underline-offset-4">
              Read our full Medical Disclaimer
            </Link>
          </div>
        </section>

      </div>
    </main>

    <Footer />
    <FloatingWhatsApp />
    <MobileBottomNav />
  </div>
);

export default EditorialPolicyPage;
